import { api, throwApiError } from "../../../api/api";
import type { DpLegalParameterType } from "./dpTypes";

export type DpEmployer = {
  id: string;
  legalName: string;
  tradeName?: string | null;
  cnpj: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  isActive: boolean;
  /** Logotipo dos PDFs: só serve de chave de cache — a imagem vem de GET /dp/employers/:id/logo. */
  logoAssetId?: string | null;
  logoFileName?: string | null;
};

export type DpPosition = {
  id: string;
  name: string;
  cbo?: string | null;
  description?: string | null;
};

export type DpCostCenter = {
  id: string;
  code: string;
  name: string;
  /** Lojas agrupadas pelo centro de custo (zero, uma ou várias). */
  stores: { id: string; nomeFantasia: string }[];
  storeIds: string[];
  isActive: boolean;
  /** Grupo ou subgrupo (DRE por centro de custo); null = sem grupo. */
  groupId?: string | null;
  group?: { id: string; code: string; name: string; parent: { id: string; code: string; name: string } | null } | null;
};

/** Grupo (raiz) ou subgrupo de centros de custo, em árvore de dois níveis. */
export type DpCostCenterGroup = {
  id: string;
  code: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  costCenters: Array<{ id: string; code: string; name: string; isActive: boolean }>;
  /** Loja do subgrupo (04/11/2026); grupo raiz não tem. */
  storeId?: string | null;
  store?: { id: string; nomeFantasia: string } | null;
  children: DpCostCenterGroup[];
};

/** Código e ordem são gerados pelo sistema (1, 2…; subgrupos 1.1, 1.2…). */
export type UpsertCostCenterGroupPayload = {
  name: string;
  parentId?: string | null;
  isActive?: boolean;
  storeId?: string | null;
};

export type UpsertEmployerPayload = {
  legalName: string;
  tradeName?: string;
  cnpj: string;
  address?: string;
  city?: string;
  state?: string;
  isActive?: boolean;
};

export type UpsertPositionPayload = {
  name: string;
  cbo?: string;
  description?: string;
};

export type UpsertCostCenterPayload = {
  code: string;
  name: string;
  /** No PATCH, a lista enviada substitui a anterior. */
  storeIds?: string[];
  isActive?: boolean;
  groupId?: string | null;
};

export type DpLegalParameter = {
  id: string;
  type: DpLegalParameterType;
  effectiveFrom: string;
  /** Derivado pelo backend dentro de cada tipo; `null` no vigente. */
  effectiveTo: string | null;
  /** Decimal → string. */
  value: string;
  notes?: string | null;
};

export type UpsertLegalParameterPayload = {
  type: DpLegalParameterType;
  effectiveFrom: string;
  value: number;
  notes?: string;
};

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

// ── Empregadores ────────────────────────────────────────────────────────────

export const listEmployersApi = async (token: string): Promise<DpEmployer[]> => {
  try {
    const { data } = await api.get<DpEmployer[]>("/dp/employers", auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar empregadores.");
    throw error;
  }
};

export const createEmployerApi = async (
  token: string,
  payload: UpsertEmployerPayload
): Promise<DpEmployer> => {
  try {
    const { data } = await api.post<DpEmployer>("/dp/employers", payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao cadastrar empregador.");
    throw error;
  }
};

export const updateEmployerApi = async (
  token: string,
  id: string,
  payload: Partial<UpsertEmployerPayload>
): Promise<DpEmployer> => {
  try {
    const { data } = await api.patch<DpEmployer>(`/dp/employers/${id}`, payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao editar empregador.");
    throw error;
  }
};

// ── Cargos ──────────────────────────────────────────────────────────────────

export const listPositionsApi = async (token: string): Promise<DpPosition[]> => {
  try {
    const { data } = await api.get<DpPosition[]>("/dp/positions", auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar cargos.");
    throw error;
  }
};

export const createPositionApi = async (
  token: string,
  payload: UpsertPositionPayload
): Promise<DpPosition> => {
  try {
    const { data } = await api.post<DpPosition>("/dp/positions", payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao cadastrar cargo.");
    throw error;
  }
};

export const updatePositionApi = async (
  token: string,
  id: string,
  payload: Partial<UpsertPositionPayload>
): Promise<DpPosition> => {
  try {
    const { data } = await api.patch<DpPosition>(`/dp/positions/${id}`, payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao editar cargo.");
    throw error;
  }
};

export const deletePositionApi = async (token: string, id: string): Promise<void> => {
  try {
    await api.delete(`/dp/positions/${id}`, auth(token));
  } catch (error) {
    throwApiError(error, "Erro ao excluir cargo.");
    throw error;
  }
};

// ── Centros de custo ────────────────────────────────────────────────────────

/** O cadastro é o mesmo no DP e no Financeiro (04/11/2026); muda só a rota, pelo papel de quem usa. */
export type CostCenterApiBase = "/dp" | "/financeiro/settings";

export const listCostCentersApi = async (
  token: string,
  includeInactive = false,
  base: CostCenterApiBase = "/dp"
): Promise<DpCostCenter[]> => {
  try {
    const { data } = await api.get<DpCostCenter[]>(`${base}/cost-centers`, {
      ...auth(token),
      params: includeInactive ? { includeInactive: "true" } : undefined,
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar centros de custo.");
    throw error;
  }
};

export const createCostCenterApi = async (
  token: string,
  payload: UpsertCostCenterPayload,
  base: CostCenterApiBase = "/dp"
): Promise<DpCostCenter> => {
  try {
    const { data } = await api.post<DpCostCenter>(`${base}/cost-centers`, payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao cadastrar centro de custo.");
    throw error;
  }
};

export const updateCostCenterApi = async (
  token: string,
  id: string,
  payload: Partial<UpsertCostCenterPayload>,
  base: CostCenterApiBase = "/dp"
): Promise<DpCostCenter> => {
  try {
    const { data } = await api.patch<DpCostCenter>(`${base}/cost-centers/${id}`, payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao editar centro de custo.");
    throw error;
  }
};

export const deleteCostCenterApi = async (token: string, id: string, base: CostCenterApiBase = "/dp"): Promise<void> => {
  try {
    await api.delete(`${base}/cost-centers/${id}`, auth(token));
  } catch (error) {
    throwApiError(error, "Erro ao excluir centro de custo.");
    throw error;
  }
};

// ── Grupos de centro de custo ───────────────────────────────────────────────

export const listCostCenterGroupsApi = async (token: string, includeInactive = false, base: CostCenterApiBase = "/dp"): Promise<DpCostCenterGroup[]> => {
  try {
    const { data } = await api.get<DpCostCenterGroup[]>(`${base}/cost-center-groups`, {
      ...auth(token),
      params: includeInactive ? { includeInactive: "true" } : undefined,
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar grupos de centro de custo.");
    throw error;
  }
};

export const createCostCenterGroupApi = async (token: string, payload: UpsertCostCenterGroupPayload, base: CostCenterApiBase = "/dp") => {
  try {
    const { data } = await api.post<DpCostCenterGroup>(`${base}/cost-center-groups`, payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao cadastrar o grupo.");
    throw error;
  }
};

export const updateCostCenterGroupApi = async (token: string, id: string, payload: Partial<UpsertCostCenterGroupPayload>, base: CostCenterApiBase = "/dp") => {
  try {
    const { data } = await api.patch<DpCostCenterGroup>(`${base}/cost-center-groups/${id}`, payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao editar o grupo.");
    throw error;
  }
};

export const deleteCostCenterGroupApi = async (token: string, id: string, base: CostCenterApiBase = "/dp"): Promise<void> => {
  try {
    await api.delete(`${base}/cost-center-groups/${id}`, auth(token));
  } catch (error) {
    throwApiError(error, "Erro ao excluir o grupo.");
    throw error;
  }
};

// ── Parâmetros legais ───────────────────────────────────────────────────────

export const listLegalParametersApi = async (token: string): Promise<DpLegalParameter[]> => {
  try {
    const { data } = await api.get<DpLegalParameter[]>("/dp/legal-parameters", auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar parâmetros legais.");
    throw error;
  }
};

export const createLegalParameterApi = async (
  token: string,
  payload: UpsertLegalParameterPayload
): Promise<DpLegalParameter> => {
  try {
    const { data } = await api.post<DpLegalParameter>("/dp/legal-parameters", payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao cadastrar parâmetro legal.");
    throw error;
  }
};

export const updateLegalParameterApi = async (
  token: string,
  id: string,
  payload: Partial<UpsertLegalParameterPayload>
): Promise<DpLegalParameter> => {
  try {
    const { data } = await api.patch<DpLegalParameter>(
      `/dp/legal-parameters/${id}`,
      payload,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao editar parâmetro legal.");
    throw error;
  }
};

export const deleteLegalParameterApi = async (token: string, id: string): Promise<void> => {
  try {
    await api.delete(`/dp/legal-parameters/${id}`, auth(token));
  } catch (error) {
    throwApiError(error, "Erro ao excluir parâmetro legal.");
    throw error;
  }
};

// ── Logotipo do empregador (cabeçalho dos PDFs) ───────────────────────────

/** PNG ou JPG, até 1 MB — o gerador de PDF não desenha WEBP. */
export const EMPLOYER_LOGO_TYPES = ["image/png", "image/jpeg"];
export const EMPLOYER_LOGO_MAX_BYTES = 1024 * 1024;

export const uploadEmployerLogoApi = async (token: string, employerId: string, file: File): Promise<void> => {
  const form = new FormData();
  form.append("file", file);
  try {
    await api.post(`/dp/employers/${employerId}/logo`, form, {
      headers: { ...auth(token).headers, "Content-Type": "multipart/form-data" },
    });
  } catch (error) {
    throwApiError(error, "Erro ao enviar o logotipo.");
    throw error;
  }
};

/** O logotipo vem como blob pela rota do DP (com o token) — um <img src> direto não mandaria o token. */
export const getEmployerLogoBlobApi = async (token: string, employerId: string): Promise<Blob> => {
  try {
    const { data } = await api.get<Blob>(`/dp/employers/${employerId}/logo`, { ...auth(token), responseType: "blob" });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar o logotipo.");
    throw error;
  }
};

export const deleteEmployerLogoApi = async (token: string, employerId: string): Promise<void> => {
  try {
    await api.delete(`/dp/employers/${employerId}/logo`, auth(token));
  } catch (error) {
    throwApiError(error, "Erro ao remover o logotipo.");
    throw error;
  }
};